/**
 * Modal Data Store Hook
 * 
 * Manages storage and retrieval of modal data payloads.
 * Encapsulates the ref-based storage logic for modal data.
 */

import * as React from "react";
import type { GlobalModalId } from "../types";

export interface ModalDataStoreReturn {
  /** Get data for a specific modal */
  getModalData: (id: GlobalModalId) => Record<string, any> | undefined;
  /** Set data for a specific modal */
  setModalData: (id: GlobalModalId, data: Record<string, any>) => void;
  /** Clear data for a specific modal */
  clearModalData: (id: GlobalModalId) => void;
  /** Clear all modal data */
  clearAllModalData: () => void;
  /** Bumps when modal payloads change so consumers re-read getModalData */
  modalDataRevision: number;
}

/**
 * Hook for managing modal data storage
 * 
 * Provides methods to store, retrieve, and clear modal data payloads.
 * Uses a ref to maintain data across renders without causing re-renders.
 * 
 * @returns Object with methods to manage modal data
 * 
 * @example
 * const { getModalData, setModalData, clearModalData } = useModalDataStore();
 * 
 * // Store data when opening a modal
 * setModalData("edit-contact", { contactId: "123", name: "John" });
 * 
 * // Retrieve data in the modal component
 * const data = getModalData("edit-contact");
 * 
 * // Clear data when closing
 * clearModalData("edit-contact");
 */
export const useModalDataStore = (): ModalDataStoreReturn => {
  const modalDataRef = React.useRef<Record<string, Record<string, any>>>({});
  const [modalDataRevision, setModalDataRevision] = React.useState(0);

  const bumpRevision = React.useCallback(() => {
    setModalDataRevision((revision) => revision + 1);
  }, []);

  const getModalData = React.useCallback((id: GlobalModalId): Record<string, any> | undefined => {
    const data = modalDataRef.current[id];

    // Debug logging
    if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
      if (data) {
        console.log(`🔍 Retrieved data for modal: ${id}`, data);
      } else {
        console.warn(`🔍 No data found for modal: ${id}`);
      }
    }

    return data;
  }, []);

  const setModalData = React.useCallback((id: GlobalModalId, data: Record<string, any>) => {
    modalDataRef.current[id] = data;
    bumpRevision();

    // Debug logging
    if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
      console.group(`📤 Storing Modal Data: ${id}`);
      console.log("Data payload:", data);
      console.log("Data keys:", Object.keys(data));
      console.log("Stored data ref:", modalDataRef.current[id]);
      console.groupEnd();
    }
  }, [bumpRevision]);

  const clearModalData = React.useCallback((id: GlobalModalId) => {
    // Debug logging
    if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
      const hadData = modalDataRef.current[id];
      console.group(`📥 Clearing Modal Data: ${id}`);
      if (hadData) {
        console.log("Cleared data:", hadData);
      } else {
        console.log("No data to clear");
      }
      console.groupEnd();
    }

    delete modalDataRef.current[id];
    bumpRevision();
  }, [bumpRevision]);

  const clearAllModalData = React.useCallback(() => {
    modalDataRef.current = {};
    bumpRevision();
  }, [bumpRevision]);

  return {
    getModalData,
    setModalData,
    clearModalData,
    clearAllModalData,
    modalDataRevision,
  };
};
